// components/PasswordField.tsx — password input with a show/hide toggle.
//
// Pilot phone-test finding (2026-07-07): the login/join password fields gave
// dots-only feedback with no way to check what was typed — on a phone
// keyboard that's a real lockout risk (and there is no self-service reset in
// the pilot). Shared by LoginScreen and JoinScreen so the field chrome stays
// identical to the other inputs on those screens.

import { useState } from 'react';
import { useTokens } from '../state/theme';

function EyeIcon({ color, off }: { color: string; off: boolean }) {
  // House line-icon style (see modeIcons.tsx): 20×20 viewBox, stroke-only.
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={color}
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 10s3-5.5 8-5.5S18 10 18 10s-3 5.5-8 5.5S2 10 2 10Z" />
      <circle cx="10" cy="10" r="2.4" />
      {off && <path d="M4 16 16 4" />}
    </svg>
  );
}

export function PasswordField({
  value,
  onChange,
  autoComplete,
  placeholder,
  disabled,
  ariaLabel = 'Password',
}: {
  value: string;
  onChange: (next: string) => void;
  autoComplete: 'current-password' | 'new-password';
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const tokens = useTokens();
  const [visible, setVisible] = useState(false);

  return (
    <div
      style={{
        background: tokens.surface,
        border: `1px solid ${tokens.lineStrong}`,
        borderRadius: tokens.radius.md,
        padding: '10px 6px 10px 14px',
        minHeight: 44,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        boxSizing: 'border-box',
      }}
    >
      <input
        style={{
          border: 'none', outline: 'none', background: 'transparent', width: '100%',
          color: tokens.text, fontFamily: tokens.fonts.body, fontSize: 15, minHeight: 24,
        }}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        aria-label={visible ? 'Nascondi password' : 'Mostra password'}
        aria-pressed={visible}
        style={{
          flex: '0 0 auto',
          width: 36,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          background: 'transparent',
          cursor: disabled ? 'default' : 'pointer',
          padding: 0,
        }}
      >
        <EyeIcon color={tokens.textMuted} off={!visible} />
      </button>
    </div>
  );
}
